import { useCallback, useState } from 'react';

// Types
import { Account } from '@/types/planning';
import GenericContact from '@/types/generic-contact';
import PortfolioRiskProfile from '@/types/portfolio-risk-profile';
import Nullable from '@/types/nullable';
import User from '@/types/user';

// Hooks
import useReconcileAccountRiskProfile from '@/hooks/planning/useReconcileAccountRiskProfile';
import { useContactPlanAccounts } from '../../../../../hooks/planning/useContactPlanAccounts';

// Utils
import { canViewPlanning } from '@/utils/portfolio/portfolio';

type UsePortfolioEditorPlanAccountProps = {
  contact: GenericContact;
  attachProfileToPortfolio: (profile: Partial<PortfolioRiskProfile>) => void;
  setPortfolioPlanAccount: (planId: string, planAccountId: string, planAccount: Account) => void;
  setPortfolioSize: (size: Nullable<number>) => void;
  user?: Nullable<User>;
};

export const usePortfolioEditorPlanAccount = ({
  contact,
  setPortfolioPlanAccount,
  setPortfolioSize,
  attachProfileToPortfolio,
  user,
}: UsePortfolioEditorPlanAccountProps) => {
  const contactId = contact?._id;
  const canViewPlan = canViewPlanning(user, contact);
  const { isLoading, importedAccounts, planId, error } = useContactPlanAccounts({ contact, skip: !contactId || !canViewPlan });

  const [showPlanAccountModal, setShowPlanAccountModal] = useState(false);
  const [showInformationReplacedModal, setShowInformationReplacedModal] = useState(false);
  const [selectedPlanAccount, setSelectedPlanAccount] = useState<Account | undefined>(undefined);

  const { getAccountRiskProfile } = useReconcileAccountRiskProfile();

  const onChangePlanAccount = useCallback(() => {
    setShowPlanAccountModal(true);
  }, []);

  const onClosePlanAccountModal = useCallback(() => {
    setShowPlanAccountModal(false);
  }, []);

  const onAddPlanAccount = useCallback(
    (planAccountId: string) => {
      const selectedAccount = importedAccounts?.find(account => account._id === planAccountId);

      setSelectedPlanAccount(selectedAccount);
      setShowPlanAccountModal(false);
      setShowInformationReplacedModal(true);
    },
    [importedAccounts],
  );

  const onConfirmPlanAccount = useCallback(() => {
    if (!selectedPlanAccount) return;

    setPortfolioPlanAccount(planId!, selectedPlanAccount?._id, selectedPlanAccount);

    const roundedSize = +selectedPlanAccount.totalValue.toFixed(2);
    setPortfolioSize(roundedSize);

    const riskProfile = getAccountRiskProfile(selectedPlanAccount);
    if (riskProfile) {
      attachProfileToPortfolio(riskProfile);
    }

    setSelectedPlanAccount(undefined);
    setShowInformationReplacedModal(false);
  }, [attachProfileToPortfolio, getAccountRiskProfile, planId, selectedPlanAccount, setPortfolioPlanAccount, setPortfolioSize]);

  const onCancelPlanAccount = useCallback(() => {
    setSelectedPlanAccount(undefined);
    setShowInformationReplacedModal(false);
  }, []);

  return {
    onAddPlanAccount,
    onConfirmPlanAccount,
    onCancelPlanAccount,
    onClosePlanAccountModal,
    onChangePlanAccount,
    selectedPlanAccount,
    showInformationReplacedModal,
    showPlanAccountModal,
    isLoadingPlan: isLoading,
    planError: error,
    importedAccounts,
  };
};
